// Admin Review → MIDTC BI. The connection to the self-hosted MIDTC BI system and every
// add / remove request the FTR (or a tech) sent it. Backend: src/biConnector.js.
import { useState, useEffect, useCallback } from "react";
import API from "../api";
import { BarChart3, RefreshCw, Check, X, KeyRound, PlugZap, Power, UserPlus, UserMinus, RotateCcw, Copy, AlertTriangle, Clock } from "lucide-react";

const panel = { background:"var(--t-surface)", border:"1px solid var(--t-border)", borderRadius:14, padding:18 };
const card  = { background:"var(--t-surface-mid)", border:"1px solid var(--t-border)", borderRadius:10 };
const btn   = { display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", border:"1px solid rgba(79,111,255,0.25)", background:"rgba(79,111,255,0.08)", color:"var(--btn-remote-text, #cdd9ef)" };
const input = { background:"var(--t-surface)", border:"1px solid var(--t-border)", borderRadius:8, padding:"8px 10px", color:"var(--t-text-primary)", fontSize:13, outline:"none", boxSizing:"border-box" };
const label = { fontSize:11.5, fontWeight:700, color:"var(--t-text-muted)", textTransform:"uppercase", letterSpacing:".05em" };
const pill  = (c) => ({ display:"inline-flex", alignItems:"center", gap:5, fontSize:11, fontWeight:700, padding:"2px 9px", borderRadius:999, background:c + "22", color:c, border:`1px solid ${c}44`, textTransform:"uppercase" });

const STATUS_COLOR = { completed:"#22c55e", not_found:"#64748b", pending:"#f59e0b", sending:"#3b82f6", accepted:"#3b82f6", failed:"#ef4444", cancelled:"#94a3b8" };
const fmt = (d) => d ? new Date(d).toLocaleString("en-US", { timeZone:"America/New_York", month:"short", day:"numeric", hour:"numeric", minute:"2-digit", timeZoneName:"short" }) : "—";

function statusOf(r) {
  if (r.status === "pending" && !(r.attempts > 0) && new Date(r.run_after) > new Date()) return { key:"scheduled", text:`Scheduled ${fmt(r.run_after)}`, c:"#a855f7" };
  if (r.status === "pending" && r.attempts > 0) return { key:"retrying", text:`Retrying (${r.attempts} tries) · next ${fmt(r.run_after)}`, c:"#f59e0b" };
  if (r.status === "pending") return { key:"queued", text:"Queued", c:"#f59e0b" };
  if (r.status === "not_found") return { key:"not_found", text:"No BI account (nothing to remove)", c:STATUS_COLOR.not_found };
  if (r.status === "accepted") return { key:"accepted", text:"Accepted by BI — awaiting confirmation", c:STATUS_COLOR.accepted };
  return { key:r.status, text:r.status.charAt(0).toUpperCase() + r.status.slice(1), c:STATUS_COLOR[r.status] || "#64748b" };
}

export default function BiAccessPanel({ onCounts }) {
  const [data, setData]       = useState(null);
  const [reqs, setReqs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);
  const [msg, setMsg]         = useState(null);           // {ok, text}
  const [baseUrl, setBaseUrl] = useState("");
  const [creds, setCreds]     = useState(null);           // shown once after issuing
  const [filter, setFilter]   = useState("all");
  const [form, setForm]       = useState({ action:"remove", client_id:"", email:"", first_name:"", last_name:"" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([API.get("/admin/bi-integration"), API.get("/admin/bi-requests")]);
      setData(s.data);
      setBaseUrl(s.data.settings.base_url || "");
      setReqs(r.data.requests || []);
      onCounts && onCounts({ failed: s.data.counts?.failed || 0 });
      setForm(f => f.client_id ? f : { ...f, client_id: Object.keys(s.data.settings.client_map || {})[0] || "" });
    } catch (e) { setMsg({ ok:false, text: e?.response?.data?.error || "Could not load the MIDTC BI settings." }); }
    finally { setLoading(false); }
  }, [onCounts]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okText) => {
    setBusy(key); setMsg(null);
    try { const r = await fn(); if (okText) setMsg({ ok:true, text: typeof okText === "function" ? okText(r) : okText }); await load(); return r; }
    catch (e) { setMsg({ ok:false, text: e?.response?.data?.error || e.message }); }
    finally { setBusy(null); }
  };

  if (loading && !data) return <div style={{ ...panel, textAlign:"center", color:"var(--t-text-muted)" }}>Loading…</div>;
  if (!data) return <div style={panel}>{msg && <div style={{ color:"#ef4444" }}>{msg.text}</div>}</div>;

  const s = data.settings;
  const clients = Object.entries(s.client_map || {});
  const conn = s.live ? { t:"LIVE", c:"#22c55e" } : s.connected ? { t:"Connected — not live", c:"#3b82f6" } : s.has_credentials ? { t:"Waiting for the BI side to connect", c:"#f59e0b" } : { t:"Not set up", c:"#94a3b8" };
  const shown = reqs.filter(r => {
    const k = statusOf(r).key;
    if (filter === "attention") return k === "failed" || k === "retrying";
    if (filter === "open") return ["queued", "scheduled", "retrying", "sending", "accepted"].includes(k);
    if (filter === "done") return ["completed", "not_found", "cancelled"].includes(k);
    return true;
  });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* ── Connection ── */}
      <div style={panel}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <BarChart3 size={18} color="#38bdf8" />
            <h2 style={{ fontSize:15, fontWeight:700, color:"var(--t-text-primary)", margin:0 }}>MIDTC BI connection</h2>
            <span style={pill(conn.c)}>{conn.t}</span>
          </div>
          <button style={btn} onClick={load} disabled={loading}><RefreshCw size={14}/></button>
        </div>
        <p style={{ color:"var(--t-text-muted)", fontSize:12.5, margin:"0 0 12px" }}>
          For <strong>{clients.map(([, c]) => c.name).join(" and ") || "—"}</strong>, the FTR adds new hires to MIDTC BI and removes terminated staff — at the same time as the Microsoft 365 change, and for a future-dated termination at its effective time. Nothing is sent until <strong>Live</strong> is on; requests made before then wait here and go out automatically.
        </p>
        {msg && <div style={{ ...card, padding:"8px 12px", marginBottom:12, fontSize:12.5, color: msg.ok ? "#22c55e" : "#f87171", borderColor: msg.ok ? "rgba(34,197,94,.35)" : "rgba(239,68,68,.35)" }}>{msg.text}</div>}

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))", gap:12 }}>
          <div style={{ ...card, padding:12 }}>
            <div style={label}>BI API address</div>
            <div style={{ display:"flex", gap:6, marginTop:6 }}>
              <input style={{ ...input, flex:1, minWidth:0 }} value={baseUrl} placeholder="https://bi.midtc.com/api/midtc/v1" onChange={e => setBaseUrl(e.target.value)} />
              <button style={btn} disabled={busy === "url" || baseUrl === (s.base_url || "")}
                onClick={() => run("url", () => API.patch("/admin/bi-integration", { base_url: baseUrl.trim() || null }), "Address saved.")}>Save</button>
            </div>
            <div style={{ fontSize:11.5, color:"var(--t-text-faint)", marginTop:6 }}>Usually set by the BI side itself with its <code>connect</code> call. Must be https.</div>
          </div>

          <div style={{ ...card, padding:12 }}>
            <div style={label}>Last connection test</div>
            <div style={{ fontSize:13, color:"var(--t-text-primary)", marginTop:6 }}>
              {s.last_health_at ? <>{s.last_health_ok ? <Check size={14} color="#22c55e" style={{ verticalAlign:"-2px" }}/> : <X size={14} color="#ef4444" style={{ verticalAlign:"-2px" }}/>} {fmt(s.last_health_at)}</> : "Never tested"}
            </div>
            {s.last_health_detail && !s.last_health_ok && <div style={{ fontSize:11.5, color:"#f87171", marginTop:4 }}>{s.last_health_detail.error || `HTTP ${s.last_health_detail.status}`}</div>}
            <div style={{ fontSize:11.5, color:"var(--t-text-faint)", marginTop:4 }}>Last call from BI: {fmt(s.last_inbound_at)}</div>
            <button style={{ ...btn, marginTop:8 }} disabled={busy === "test" || !s.base_url}
              onClick={() => run("test", () => API.post("/admin/bi-integration/test"), r => r.data.ok ? `Connection OK (${r.data.latency_ms} ms).` : `Connection FAILED: ${r.data.error || "HTTP " + r.data.status}`)}>
              <PlugZap size={14}/> Test connection
            </button>
          </div>

          <div style={{ ...card, padding:12 }}>
            <div style={label}>Live</div>
            <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", marginTop:6 }}>{s.live ? "Requests are being sent to BI." : "Off — requests are held here."}</div>
            <button style={{ ...btn, marginTop:8, ...(!s.enabled && s.connected ? { background:"#16a34a", color:"#fff", border:"1px solid #16a34a" } : {}), ...(!s.enabled && !s.connected ? { opacity:0.45, cursor:"not-allowed" } : {}) }}
              disabled={busy === "live" || (!s.enabled && !s.connected)}
              onClick={() => run("live", () => API.patch("/admin/bi-integration", { enabled: !s.enabled }), s.enabled ? "Paused — nothing will be sent until Live is turned back on." : "Live — queued requests are going out now.")}>
              <Power size={14}/> {s.enabled ? "Pause" : "Go live"}
            </button>
            {!s.connected && <div style={{ fontSize:11.5, color:"var(--t-text-faint)", marginTop:6 }}>Available once the BI side has connected.</div>}
          </div>

          <div style={{ ...card, padding:12 }}>
            <div style={label}>New hires</div>
            <select style={{ ...input, width:"100%", marginTop:6 }} value={s.provision_mode} disabled={busy === "mode"}
              onChange={e => run("mode", () => API.patch("/admin/bi-integration", { provision_mode: e.target.value }), "Saved.")}>
              <option value="all_new_hires">Every new hire gets a BI account</option>
              <option value="requested_only">Only when the new-hire form asks for BI</option>
            </select>
            <div style={{ fontSize:11.5, color:"var(--t-text-faint)", marginTop:6 }}>Terminations always remove BI access.</div>
          </div>

          <div style={{ ...card, padding:12 }}>
            <div style={label}>Credentials</div>
            <div style={{ fontSize:12.5, color:"var(--t-text-secondary)", marginTop:6 }}>
              {s.has_credentials ? <>Issued {fmt(s.credentials_rotated_at)} · BI key <code>{s.inbound_key_prefix}…</code></> : "Not issued yet."}
            </div>
            <div style={{ fontSize:11.5, color:"var(--t-text-faint)", marginTop:4 }}>Ticketing calls BI from <code>{data.egress_ip}</code>.</div>
            <button style={{ ...btn, marginTop:8 }} disabled={busy === "creds"}
              onClick={() => {
                if (s.has_credentials && !window.confirm("Issue NEW credentials? The current ones stop working immediately and the BI side must be updated with the new values.")) return;
                run("creds", async () => { const r = await API.post("/admin/bi-integration/credentials"); setCreds(r.data); return r; }, "New credentials issued — copy them below. They are shown only once.");
              }}>
              <KeyRound size={14}/> {s.has_credentials ? "Rotate credentials" : "Issue credentials"}
            </button>
          </div>
        </div>

        {creds && (
          <div style={{ ...card, padding:12, marginTop:12, borderLeft:"3px solid #22c55e" }}>
            <div style={{ fontSize:12.5, fontWeight:700, color:"var(--t-text-primary)", marginBottom:6 }}>Give these to the BI developer (shown once)</div>
            {creds.secure_link && (
              <div style={{ fontSize:12.5, marginBottom:8, color:"var(--t-text-secondary)" }}>
                Secure link (24 h): <a href={creds.secure_link} target="_blank" rel="noreferrer" style={{ color:"#60a5fa", wordBreak:"break-all" }}>{creds.secure_link}</a>
                <button style={{ ...btn, padding:"3px 8px", marginLeft:6 }} onClick={() => navigator.clipboard?.writeText(creds.secure_link)}><Copy size={12}/></button>
              </div>
            )}
            <pre style={{ margin:0, fontSize:12, background:"var(--t-surface)", border:"1px solid var(--t-border)", borderRadius:8, padding:10, whiteSpace:"pre-wrap", wordBreak:"break-all", color:"var(--t-text-primary)" }}>
              {Object.entries(creds.credentials).map(([k, v]) => `${k}=${v}`).join("\n")}
            </pre>
            <button style={{ ...btn, marginTop:8 }} onClick={() => setCreds(null)}>I've copied them — hide</button>
          </div>
        )}
      </div>

      {/* ── Requests ── */}
      <div style={panel}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:10 }}>
          <h2 style={{ fontSize:15, fontWeight:700, color:"var(--t-text-primary)", margin:0 }}>Add / remove requests</h2>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {[["all","All"],["attention","Needs attention"],["open","Open"],["done","Done"]].map(([k, t]) => (
              <button key={k} onClick={() => setFilter(k)} style={{ ...btn, padding:"5px 11px", fontSize:12, ...(filter === k ? { background:"rgba(56,189,248,.18)", border:"1px solid rgba(56,189,248,.5)", color:"var(--t-text-primary)" } : {}) }}>{t}</button>
            ))}
          </div>
        </div>
        {shown.length === 0 ? (
          <div style={{ ...card, padding:24, textAlign:"center", color:"var(--t-text-muted)" }}>No requests{filter !== "all" ? " in this view" : " yet"}.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {shown.map(r => {
              const st = statusOf(r);
              return (
                <div key={r.id} style={{ ...card, padding:12, borderLeft:`3px solid ${st.c}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span style={pill(r.action === "add" ? "#22d3ee" : "#fb7185")}>{r.action === "add" ? <UserPlus size={11}/> : <UserMinus size={11}/>}{r.action === "add" ? "Add" : "Remove"}</span>
                    <span style={{ fontSize:13.5, fontWeight:700, color:"var(--t-text-primary)" }}>{r.display_name || [r.first_name, r.last_name].filter(Boolean).join(" ") || r.email}</span>
                    <span style={{ fontSize:12, color:"var(--t-text-muted)", wordBreak:"break-all" }}>&lt;{r.email}&gt;</span>
                    <span style={pill(st.c)}>{st.key === "scheduled" ? <Clock size={11}/> : st.key === "failed" ? <AlertTriangle size={11}/> : null}{st.text}</span>
                    <span style={{ marginLeft:"auto", fontSize:11.5, color:"var(--t-text-faint)" }}>#{r.id} · {fmt(r.created_at)}</span>
                  </div>
                  <div style={{ fontSize:12, color:"var(--t-text-muted)", marginTop:5 }}>
                    {[r.client_code, r.ticket_number ? `ticket TKT-${r.ticket_number}` : null, r.source === "admin" ? `by ${r.requested_by}` : r.source ? `from ${r.source.replace("ftr_", "FTR ")}` : null,
                      r.bi_status ? `BI said: ${r.bi_status}` : null, r.completed_at ? `done ${fmt(r.completed_at)}` : null].filter(Boolean).join(" · ")}
                  </div>
                  {r.last_error && !["completed", "not_found"].includes(r.status) && (
                    <div style={{ fontSize:12, color:"#f87171", marginTop:5 }}>{r.last_http_status ? `HTTP ${r.last_http_status} — ` : ""}{r.last_error}</div>
                  )}
                  {["failed", "pending", "cancelled", "accepted"].includes(r.status) && (
                    <div style={{ display:"flex", gap:6, marginTop:8 }}>
                      {["failed", "pending", "cancelled"].includes(r.status) && (
                        <button style={{ ...btn, padding:"5px 11px", fontSize:12 }} disabled={busy === "r" + r.id}
                          onClick={() => run("r" + r.id, () => API.post(`/admin/bi-requests/${r.id}/retry`), "Re-sent.")}><RotateCcw size={12}/> {r.status === "pending" ? "Send now" : "Retry"}</button>
                      )}
                      {["failed", "pending", "accepted"].includes(r.status) && (
                        <button style={{ ...btn, padding:"5px 11px", fontSize:12, color:"var(--t-text-secondary)" }} disabled={busy === "c" + r.id}
                          onClick={() => window.confirm(`Cancel this ${r.action} request for ${r.email}?`) && run("c" + r.id, () => API.post(`/admin/bi-requests/${r.id}/cancel`), "Cancelled.")}><X size={12}/> Cancel</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Manual request ── */}
      <div style={panel}>
        <h2 style={{ fontSize:15, fontWeight:700, color:"var(--t-text-primary)", margin:"0 0 6px" }}>Add or remove someone by hand</h2>
        <p style={{ color:"var(--t-text-muted)", fontSize:12.5, margin:"0 0 10px" }}>For a termination or new hire handled outside the FTR. It is queued like any other request (sent at once when Live).</p>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <select style={input} value={form.action} onChange={e => setForm({ ...form, action: e.target.value })}>
            <option value="remove">Remove BI access</option>
            <option value="add">Create BI account</option>
          </select>
          <select style={input} value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}>
            {clients.map(([id, c]) => <option key={id} value={id}>{c.name}</option>)}
          </select>
          <input style={{ ...input, minWidth:220, flex:"1 1 220px" }} placeholder="work email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <input style={{ ...input, width:130 }} placeholder="first name" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
          <input style={{ ...input, width:130 }} placeholder="last name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
          <button style={{ ...btn, background:"#0284c7", color:"#fff", border:"1px solid #0284c7" }} disabled={busy === "new" || !form.email.trim() || !form.client_id}
            onClick={() => run("new", () => API.post("/admin/bi-requests", { ...form, email: form.email.trim() }), r => `Request #${r.data.request_id}: ${r.data.status}.`)
              .then(r => r && setForm(f => ({ ...f, email:"", first_name:"", last_name:"" })))}>
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
